import { ComponentType } from '@/components/ComponentDefs';
import { BehaviorRule, ConditionType, ActionType, TargetModifier } from '@/behaviors/BehaviorDefs';

export interface RobotTemplate {
  name: string;
  label: string;
  color: string;
  bg: string;
  components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[];
  rules: BehaviorRule[];
}

// ── Mutable live templates (loaded from JSON at boot, falls back to hardcoded) ──
let liveTemplates: RobotTemplate[] | null = null;

/** Returns the current template list (live JSON if loaded, otherwise hardcoded). */
export function getTemplates(): readonly RobotTemplate[] {
  return liveTemplates ?? ROBOT_TEMPLATES;
}

/** Returns a mutable reference to the live templates for editing (dev only). */
export function getMutableTemplates(): RobotTemplate[] {
  if (!liveTemplates) liveTemplates = ROBOT_TEMPLATES.map(t => JSON.parse(JSON.stringify(t)));
  return liveTemplates;
}

/** Load templates from public/robot-templates.json at boot. */
export async function loadTemplatesFromJSON(): Promise<void> {
  try {
    const resp = await fetch('/robot-templates.json');
    if (!resp.ok) return; // no JSON file — use hardcoded
    const json: RobotTemplate[] = await resp.json();
    if (Array.isArray(json) && json.length > 0) {
      liveTemplates = json;
      console.log('[TEMPLATES] Loaded %d templates from JSON', json.length);
    }
  } catch (e) {
    console.warn('[TEMPLATES] Failed to load robot-templates.json:', e);
  }
}

/** Save current live templates to disk via Vite dev endpoint. */
export async function saveTemplatesToDisk(): Promise<boolean> {
  const templates = getMutableTemplates();
  try {
    const resp = await fetch('/api/save-templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(templates, null, 2),
    });
    if (resp.ok) {
      console.log('[TEMPLATES] Saved %d templates to disk', templates.length);
      return true;
    }
    console.warn('[TEMPLATES] Save failed:', resp.status);
    return false;
  } catch (e) {
    console.warn('[TEMPLATES] Save endpoint unavailable (production?):', e);
    return false;
  }
}

export const ROBOT_TEMPLATES: readonly RobotTemplate[] = [
  {
    // Cheapest static unit — affordable at round 1 (cost ~20)
    // Sensor on top of structure (structural base), cannon on right, battery below
    name: 'Sentry', label: 'Sentry', color: '#88aa88', bg: '#1a221a',
    components: [
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -20 },
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.CANNON_LIGHT, lx: 20, ly: 0 },
      { type: ComponentType.BATTERY, lx: 0, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Static turret — no movement, no DIRECTION
    // Sensor on top, cannon + armor mid, structure base (battery on side — top edge blocked)
    name: 'Turret', label: 'Turret', color: '#66aaff', bg: '#1a2244',
    components: [
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -20 },
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.CANNON_HEAVY, lx: -20, ly: 0 },
      { type: ComponentType.STRUCTURE, lx: -20, ly: 20 },
      { type: ComponentType.STRUCTURE, lx: 0, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Wheeled tank — DIRECTION right, wheels at bottom
    name: 'Tank', label: 'Tank', color: '#44ff88', bg: '#1a3322',
    components: [
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.STRUCTURE, lx: -20, ly: 0 },
      { type: ComponentType.DIRECTION, lx: 20, ly: 0 },             // direction → right (rot=0)
      { type: ComponentType.CANNON_LIGHT, lx: -20, ly: -20 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: 20, ly: -20 },
      { type: ComponentType.WHEEL, lx: -20, ly: 20 },
      { type: ComponentType.WHEEL, lx: 0, ly: 20 },
      { type: ComponentType.WHEEL, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.CLOSEST },
      { priority: 3, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // Flying hunter — forward = UP (-π/2)
    // Layout vertical:
    //              Cannon(-π/2)
    //              Sensor
    //              Direction(-π/2)
    //              Structure
    //  Wing(0)  Battery  Thruster(π/2)  Battery  Wing(π)
    name: 'Hunter', label: 'Hunter', color: '#ffaa44', bg: '#332211',
    components: [
      { type: ComponentType.MISSILE_SAM, lx: 0, ly: -40, rotation: -Math.PI / 2 }, // barrel up
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -20 },
      { type: ComponentType.DIRECTION, lx: 0, ly: 0, rotation: -Math.PI / 2 },    // forward = up
      { type: ComponentType.STRUCTURE, lx: 0, ly: 20 },
      { type: ComponentType.WING, lx: -20, ly: 0 },                                // wings at CoM level
      { type: ComponentType.WING, lx: 20, ly: 0, rotation: Math.PI },
      { type: ComponentType.THRUSTER, lx: 0, ly: 40, rotation: Math.PI / 2 },     // exhaust down
      { type: ComponentType.BATTERY, lx: -20, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.CLOSEST },
      { priority: 3, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // Heavy bomber — forward = UP (-π/2)
    // Layout 3×4:
    //                Sensor
    //   Cannon(-π/2) Direction(-π/2) Cannon(-π/2)
    //   Wing(0)      Armor           Wing(π)
    //   Battery      Structure       Battery
    name: 'Bomber', label: 'Bomber', color: '#ff6666', bg: '#331a1a',
    components: [
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.DIRECTION, lx: 0, ly: -20, rotation: -Math.PI / 2 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -40 },
      { type: ComponentType.WING, lx: -20, ly: 0 },                                 // right edge → center
      { type: ComponentType.WING, lx: 20, ly: 0, rotation: Math.PI },               // right→left → center
      { type: ComponentType.CANNON_HEAVY, lx: -20, ly: -20, rotation: -Math.PI / 2 }, // barrel up
      { type: ComponentType.CANNON_HEAVY, lx: 20, ly: -20, rotation: -Math.PI / 2 }, // barrel up
      { type: ComponentType.BATTERY, lx: -20, ly: 20 },                             // connects right to Structure
      { type: ComponentType.STRUCTURE, lx: 0, ly: 20 },                             // connects up to Armor
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },                              // connects left to Structure
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 3, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // Fast wheeled scout — DIRECTION right, 3 wheels, long range sensor
    name: 'Scout', label: 'Scout', color: '#ccff44', bg: '#222211',
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.DIRECTION, lx: 20, ly: 0 },
      { type: ComponentType.SENSOR_MEDIUM, lx: -20, ly: 0 },
      { type: ComponentType.BURST_CANNON, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: -20 },
      { type: ComponentType.WHEEL, lx: -20, ly: 20 },
      { type: ComponentType.WHEEL, lx: 0, ly: 20 },
      { type: ComponentType.WHEEL, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.CLOSEST },
      { priority: 3, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // Fixed recharge station — holds position, recharges & repairs nearby allies
    name: 'Charger', label: 'Charger', color: '#44ffcc', bg: '#0a2222',
    components: [
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.RECHARGE_MODULE, lx: -20, ly: 0 },
      { type: ComponentType.RECHARGE_MODULE, lx: 20, ly: 0 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: -20 },
      { type: ComponentType.BATTERY, lx: 20, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: 20 },
      { type: ComponentType.REPAIR_MODULE, lx: 0, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.BASE_DAMAGED, action: ActionType.REPAIR_BASE },
      { priority: 2, condition: ConditionType.ALLY_NEARBY_DAMAGED, action: ActionType.REPAIR_ALLY },
      { priority: 3, condition: ConditionType.ALLY_LOW_ENERGY, action: ActionType.RECHARGE_ALLY },
      { priority: 4, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Mobile recharger — wheeled support unit that seeks out depleted allies
    name: 'Medic', label: 'Medic', color: '#66ffaa', bg: '#0a2a1a',
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.DIRECTION, lx: 20, ly: 0 },
      { type: ComponentType.RECHARGE_MODULE, lx: -20, ly: 0 },
      { type: ComponentType.REPAIR_MODULE, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: -20 },
      { type: ComponentType.BATTERY, lx: 20, ly: -20 },
      { type: ComponentType.WHEEL, lx: -20, ly: 20 },
      { type: ComponentType.WHEEL, lx: 0, ly: 20 },
      { type: ComponentType.WHEEL, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.BASE_DAMAGED, action: ActionType.REPAIR_BASE },
      { priority: 2, condition: ConditionType.ALLY_NEARBY_DAMAGED, action: ActionType.REPAIR_ALLY },
      { priority: 3, condition: ConditionType.ALLY_LOW_ENERGY, action: ActionType.RECHARGE_ALLY },
      { priority: 4, condition: ConditionType.LOW_ENERGY, action: ActionType.SEEK_RECHARGER },
      { priority: 5, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // Flamethrower tank — short range AoE, heavy armor, wheels
    name: 'Scorcher', label: 'Scorcher', color: '#ff8844', bg: '#2a1a0a',
    components: [
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.DIRECTION, lx: 20, ly: 0 },
      { type: ComponentType.FLAMETHROWER, lx: -20, ly: -20 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: 20, ly: -20 },
      { type: ComponentType.RECHARGE_MODULE, lx: -20, ly: 0 },
      { type: ComponentType.WHEEL, lx: -20, ly: 20 },
      { type: ComponentType.WHEEL, lx: 0, ly: 20 },
      { type: ComponentType.WHEEL, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.CLOSEST },
      { priority: 3, condition: ConditionType.LOW_ENERGY, action: ActionType.SEEK_RECHARGER },
      { priority: 4, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // Laser sniper — long range beam, static, high energy consumption
    // Sensor on top for visibility, heavy base at bottom for stability
    name: 'Sniper', label: 'Sniper', color: '#ff44aa', bg: '#2a0a1a',
    components: [
      { type: ComponentType.ARMOR, lx: -20, ly: -20 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: 0 },
      { type: ComponentType.LASER, lx: 0, ly: 0 },
      { type: ComponentType.BATTERY, lx: 20, ly: 0 },
      { type: ComponentType.BATTERY, lx: -20, ly: 20 },
      { type: ComponentType.STRUCTURE, lx: 0, ly: 20 },
      { type: ComponentType.RECHARGE_MODULE, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 2, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Helicopter gunship — turbine flight, mobile support + light weapon
    name: 'Copter', label: 'Copter', color: '#bb88ff', bg: '#1a0a2a',
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.DIRECTION, lx: 0, ly: -20, rotation: -Math.PI / 2 },
      { type: ComponentType.TURBINE, lx: 0, ly: 20 },
      { type: ComponentType.MISSILE_SAM, lx: -20, ly: 0 },
      { type: ComponentType.SENSOR_SHORT, lx: 20, ly: 0 },
      { type: ComponentType.BATTERY, lx: -20, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
      { type: ComponentType.BATTERY, lx: -20, ly: -20 },
      { type: ComponentType.REPAIR_MODULE, lx: 20, ly: -20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ALLY_NEARBY_DAMAGED, action: ActionType.REPAIR_ALLY },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 3, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.CLOSEST },
      { priority: 4, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // Mine layer — static, drops proximity mines, stun + pushback + chain reactions
    name: 'Mine', label: 'Mine', color: '#aaaa44', bg: '#1a1a0a',
    components: [
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.MINE_LAYER, lx: -20, ly: 0 },
      { type: ComponentType.MINE_LAYER, lx: 20, ly: 0 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: -20 },
      { type: ComponentType.BATTERY, lx: 20, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: 20 },
      { type: ComponentType.RECHARGE_MODULE, lx: 0, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Missile battery — static SAM launcher, anti-air + ground
    name: 'Missile', label: 'Missile', color: '#ff6688', bg: '#2a0a1a',
    components: [
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.MISSILE_SAM, lx: -20, ly: -20 },
      { type: ComponentType.MISSILE_SAM, lx: 20, ly: -20 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: 0 },
      { type: ComponentType.BATTERY, lx: 20, ly: 0 },
      { type: ComponentType.BATTERY, lx: -20, ly: 20 },
      { type: ComponentType.RECHARGE_MODULE, lx: 0, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Fortress — heavy armor, dual cannons, no movement
    name: 'Fortress', label: 'Fortress', color: '#8888cc', bg: '#14142a',
    components: [
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.ARMOR, lx: -20, ly: 0 },
      { type: ComponentType.ARMOR, lx: 20, ly: 0 },
      { type: ComponentType.CANNON_HEAVY, lx: -20, ly: -20 },
      { type: ComponentType.CANNON_HEAVY, lx: 20, ly: -20 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: 20 },
      { type: ComponentType.RECHARGE_MODULE, lx: 0, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 2, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Blocker — stab legs, blocks enemy path, self-repair
    name: 'Blocker', label: 'Blocker', color: '#88ccaa', bg: '#0a2a1a',
    components: [
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.ARMOR, lx: -20, ly: 0 },
      { type: ComponentType.ARMOR, lx: 20, ly: 0 },
      { type: ComponentType.STAB_LEGS, lx: 0, ly: 20 },
      { type: ComponentType.REPAIR_MODULE, lx: -20, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: -20 },
      { type: ComponentType.BATTERY, lx: 20, ly: -20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Burst runner — fast wheeled with burst cannon, hit and run
    name: 'Raider', label: 'Raider', color: '#ffcc44', bg: '#2a2200',
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.DIRECTION, lx: 20, ly: 0 },
      { type: ComponentType.BURST_CANNON, lx: -20, ly: -20 },
      { type: ComponentType.BURST_CANNON, lx: 20, ly: -20 },
      { type: ComponentType.SENSOR_SHORT, lx: 0, ly: -20 },
      { type: ComponentType.BATTERY, lx: -20, ly: 0 },
      { type: ComponentType.WHEEL, lx: -20, ly: 20 },
      { type: ComponentType.WHEEL, lx: 0, ly: 20 },
      { type: ComponentType.WHEEL, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.CLOSEST },
      { priority: 3, condition: ConditionType.LOW_ENERGY, action: ActionType.SEEK_RECHARGER },
      { priority: 4, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // Ground missile truck — mobile sol-sol launcher
    name: 'Launcher', label: 'Launcher', color: '#cc8844', bg: '#221a0a',
    components: [
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.DIRECTION, lx: 20, ly: 0 },
      { type: ComponentType.MISSILE_SOL_SOL, lx: 0, ly: -20 },
      { type: ComponentType.SENSOR_MEDIUM, lx: -20, ly: 0 },
      { type: ComponentType.BATTERY, lx: -20, ly: -20 },
      { type: ComponentType.BATTERY, lx: 20, ly: -20 },
      { type: ComponentType.WHEEL, lx: -20, ly: 20 },
      { type: ComponentType.WHEEL, lx: 0, ly: 20 },
      { type: ComponentType.WHEEL, lx: 20, ly: 20 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 3, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  // ── 4×4 templates (require grid ≥ 4) ──
  {
    // Heavy assault platform — dual weapons, thick armor front, wheeled
    name: 'Juggernaut', label: 'Juggernaut', color: '#cc6644', bg: '#2a1408',
    components: [
      // Row -30: weapons + sensor
      { type: ComponentType.CANNON_HEAVY, lx: -20, ly: -30 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -30 },
      { type: ComponentType.CANNON_HEAVY, lx: 20, ly: -30 },
      // Row -10: armor wall
      { type: ComponentType.ARMOR, lx: -30, ly: -10 },
      { type: ComponentType.ARMOR, lx: -10, ly: -10 },
      { type: ComponentType.ARMOR, lx: 10, ly: -10 },
      { type: ComponentType.ARMOR, lx: 30, ly: -10 },
      // Row 10: core + energy
      { type: ComponentType.BATTERY, lx: -30, ly: 10 },
      { type: ComponentType.STRUCTURE, lx: -10, ly: 10 },
      { type: ComponentType.DIRECTION, lx: 10, ly: 10 },
      { type: ComponentType.BATTERY, lx: 30, ly: 10 },
      // Row 30: wheels + recharge
      { type: ComponentType.WHEEL, lx: -30, ly: 30 },
      { type: ComponentType.WHEEL, lx: -10, ly: 30 },
      { type: ComponentType.RECHARGE_MODULE, lx: 10, ly: 30 },
      { type: ComponentType.WHEEL, lx: 30, ly: 30 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 3, condition: ConditionType.LOW_ENERGY, action: ActionType.SEEK_RECHARGER },
      { priority: 4, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // AA battery — 4×4 static anti-air platform with SAMs + air cannon
    name: 'Skyguard', label: 'Skyguard', color: '#6688ff', bg: '#0a1433',
    components: [
      // Row -30: missiles
      { type: ComponentType.MISSILE_SAM, lx: -10, ly: -30 },
      { type: ComponentType.MISSILE_SAM, lx: 10, ly: -30 },
      // Row -10: weapons + sensor
      { type: ComponentType.MISSILE_SAM, lx: -30, ly: -10 },
      { type: ComponentType.SENSOR_MEDIUM, lx: -10, ly: -10 },
      { type: ComponentType.ARMOR, lx: 10, ly: -10 },
      { type: ComponentType.MISSILE_SAM, lx: 30, ly: -10 },
      // Row 10: core
      { type: ComponentType.BATTERY, lx: -30, ly: 10 },
      { type: ComponentType.STRUCTURE, lx: -10, ly: 10 },
      { type: ComponentType.RECHARGE_MODULE, lx: 10, ly: 10 },
      { type: ComponentType.BATTERY, lx: 30, ly: 10 },
      // Row 30: power + anchoring
      { type: ComponentType.BATTERY, lx: -30, ly: 30 },
      { type: ComponentType.BATTERY, lx: -10, ly: 30 },
      { type: ComponentType.STAB_LEGS, lx: 10, ly: 30 },
      { type: ComponentType.BATTERY, lx: 30, ly: 30 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 2, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Field hospital — 4×4 mobile support, heavy repair + recharge
    name: 'Ambulance', label: 'Ambulance', color: '#44ddaa', bg: '#082a1a',
    components: [
      // Row -30: support modules
      { type: ComponentType.REPAIR_MODULE, lx: -10, ly: -30 },
      { type: ComponentType.REPAIR_MODULE, lx: 10, ly: -30 },
      // Row -10: sensor + recharge
      { type: ComponentType.RECHARGE_MODULE, lx: -30, ly: -10 },
      { type: ComponentType.SENSOR_MEDIUM, lx: -10, ly: -10 },
      { type: ComponentType.ARMOR, lx: 10, ly: -10 },
      { type: ComponentType.RECHARGE_MODULE, lx: 30, ly: -10 },
      // Row 10: core + batteries
      { type: ComponentType.BATTERY, lx: -30, ly: 10 },
      { type: ComponentType.STRUCTURE, lx: -10, ly: 10 },
      { type: ComponentType.DIRECTION, lx: 10, ly: 10 },
      { type: ComponentType.BATTERY, lx: 30, ly: 10 },
      // Row 30: wheels
      { type: ComponentType.WHEEL, lx: -30, ly: 30 },
      { type: ComponentType.WHEEL, lx: -10, ly: 30 },
      { type: ComponentType.WHEEL, lx: 10, ly: 30 },
      { type: ComponentType.WHEEL, lx: 30, ly: 30 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.BASE_DAMAGED, action: ActionType.REPAIR_BASE },
      { priority: 2, condition: ConditionType.ALLY_NEARBY_DAMAGED, action: ActionType.REPAIR_ALLY },
      { priority: 3, condition: ConditionType.ALLY_LOW_ENERGY, action: ActionType.RECHARGE_ALLY },
      { priority: 4, condition: ConditionType.LOW_ENERGY, action: ActionType.SEEK_RECHARGER },
      { priority: 5, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  // ── 5×5 templates (require grid ≥ 5) ──
  {
    // Titan — 5×5 mega-tank, triple weapons, deep armor, self-sustaining
    name: 'Titan', label: 'Titan', color: '#dd8844', bg: '#2a1a08',
    components: [
      // Row -40: weapons
      { type: ComponentType.CANNON_HEAVY, lx: -20, ly: -40 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -40 },
      { type: ComponentType.CANNON_HEAVY, lx: 20, ly: -40 },
      // Row -20: armor + laser
      { type: ComponentType.ARMOR, lx: -40, ly: -20 },
      { type: ComponentType.ARMOR, lx: -20, ly: -20 },
      { type: ComponentType.LASER, lx: 0, ly: -20 },
      { type: ComponentType.ARMOR, lx: 20, ly: -20 },
      { type: ComponentType.ARMOR, lx: 40, ly: -20 },
      // Row 0: core
      { type: ComponentType.BATTERY, lx: -40, ly: 0 },
      { type: ComponentType.RECHARGE_MODULE, lx: -20, ly: 0 },
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.DIRECTION, lx: 20, ly: 0 },
      { type: ComponentType.BATTERY, lx: 40, ly: 0 },
      // Row 20: support + battery
      { type: ComponentType.BATTERY, lx: -40, ly: 20 },
      { type: ComponentType.REPAIR_MODULE, lx: -20, ly: 20 },
      { type: ComponentType.BATTERY, lx: 0, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
      { type: ComponentType.BATTERY, lx: 40, ly: 20 },
      // Row 40: wheels
      { type: ComponentType.WHEEL, lx: -40, ly: 40 },
      { type: ComponentType.WHEEL, lx: -20, ly: 40 },
      { type: ComponentType.WHEEL, lx: 0, ly: 40 },
      { type: ComponentType.WHEEL, lx: 20, ly: 40 },
      { type: ComponentType.WHEEL, lx: 40, ly: 40 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 2, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 3, condition: ConditionType.LOW_ENERGY, action: ActionType.SEEK_RECHARGER },
      { priority: 4, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
  {
    // Citadel — 5×5 immovable fortress, maximum firepower + stab legs
    name: 'Citadel', label: 'Citadel', color: '#9988cc', bg: '#14102a',
    components: [
      // Row -40: weapons
      { type: ComponentType.MISSILE_SAM, lx: -40, ly: -40 },
      { type: ComponentType.CANNON_HEAVY, lx: -20, ly: -40 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -40 },
      { type: ComponentType.CANNON_HEAVY, lx: 20, ly: -40 },
      { type: ComponentType.MISSILE_SAM, lx: 40, ly: -40 },
      // Row -20: armor belt
      { type: ComponentType.ARMOR, lx: -40, ly: -20 },
      { type: ComponentType.ARMOR, lx: -20, ly: -20 },
      { type: ComponentType.ARMOR, lx: 0, ly: -20 },
      { type: ComponentType.ARMOR, lx: 20, ly: -20 },
      { type: ComponentType.ARMOR, lx: 40, ly: -20 },
      // Row 0: core + flamethrowers
      { type: ComponentType.FLAMETHROWER, lx: -40, ly: 0 },
      { type: ComponentType.BATTERY, lx: -20, ly: 0 },
      { type: ComponentType.STRUCTURE, lx: 0, ly: 0 },
      { type: ComponentType.BATTERY, lx: 20, ly: 0 },
      { type: ComponentType.FLAMETHROWER, lx: 40, ly: 0 },
      // Row 20: power + recharge
      { type: ComponentType.BATTERY, lx: -40, ly: 20 },
      { type: ComponentType.RECHARGE_MODULE, lx: -20, ly: 20 },
      { type: ComponentType.BATTERY, lx: 0, ly: 20 },
      { type: ComponentType.RECHARGE_MODULE, lx: 20, ly: 20 },
      { type: ComponentType.BATTERY, lx: 40, ly: 20 },
      // Row 40: stab legs + repair + mines
      { type: ComponentType.STAB_LEGS, lx: -40, ly: 40 },
      { type: ComponentType.MINE_LAYER, lx: -20, ly: 40 },
      { type: ComponentType.REPAIR_MODULE, lx: 0, ly: 40 },
      { type: ComponentType.MINE_LAYER, lx: 20, ly: 40 },
      { type: ComponentType.STAB_LEGS, lx: 40, ly: 40 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.MOST_ADVANCED },
      { priority: 2, condition: ConditionType.ALWAYS, action: ActionType.HOLD_POSITION },
    ],
  },
  {
    // Carrier — 5×5 flying command ship, mixed weapons + full support suite
    name: 'Carrier', label: 'Carrier', color: '#cc88ff', bg: '#1a0833',
    components: [
      // Row -40: nose weapons
      { type: ComponentType.MISSILE_SAM, lx: -20, ly: -40, rotation: -Math.PI / 2 },
      { type: ComponentType.SENSOR_MEDIUM, lx: 0, ly: -40 },
      { type: ComponentType.MISSILE_SAM, lx: 20, ly: -40, rotation: -Math.PI / 2 },
      // Row -20: wings + direction
      { type: ComponentType.WING, lx: -40, ly: -20 },
      { type: ComponentType.MISSILE_SAM, lx: -20, ly: -20 },
      { type: ComponentType.DIRECTION, lx: 0, ly: -20, rotation: -Math.PI / 2 },
      { type: ComponentType.MISSILE_SAM, lx: 20, ly: -20 },
      { type: ComponentType.WING, lx: 40, ly: -20, rotation: Math.PI },
      // Row 0: core
      { type: ComponentType.BATTERY, lx: -40, ly: 0 },
      { type: ComponentType.REPAIR_MODULE, lx: -20, ly: 0 },
      { type: ComponentType.ARMOR, lx: 0, ly: 0 },
      { type: ComponentType.RECHARGE_MODULE, lx: 20, ly: 0 },
      { type: ComponentType.BATTERY, lx: 40, ly: 0 },
      // Row 20: engine + batteries
      { type: ComponentType.BATTERY, lx: -40, ly: 20 },
      { type: ComponentType.BATTERY, lx: -20, ly: 20 },
      { type: ComponentType.STRUCTURE, lx: 0, ly: 20 },
      { type: ComponentType.BATTERY, lx: 20, ly: 20 },
      { type: ComponentType.BATTERY, lx: 40, ly: 20 },
      // Row 40: thrusters
      { type: ComponentType.BATTERY, lx: -40, ly: 40 },
      { type: ComponentType.THRUSTER, lx: -20, ly: 40, rotation: Math.PI / 2 },
      { type: ComponentType.TURBINE, lx: 0, ly: 40 },
      { type: ComponentType.THRUSTER, lx: 20, ly: 40, rotation: Math.PI / 2 },
      { type: ComponentType.BATTERY, lx: 40, ly: 40 },
    ],
    rules: [
      { priority: 1, condition: ConditionType.ALLY_NEARBY_DAMAGED, action: ActionType.REPAIR_ALLY },
      { priority: 2, condition: ConditionType.ALLY_LOW_ENERGY, action: ActionType.RECHARGE_ALLY },
      { priority: 3, condition: ConditionType.ENEMY_DETECTED, action: ActionType.SHOOT, targetModifier: TargetModifier.CLOSEST },
      { priority: 4, condition: ConditionType.ENEMY_DETECTED, action: ActionType.CHASE, targetModifier: TargetModifier.CLOSEST },
      { priority: 5, condition: ConditionType.ALWAYS, action: ActionType.PATROL },
    ],
  },
];
